import React from "react";
import { EyeFilled } from "@ant-design/icons";
import { ImageMask } from "../imageMask";
import { useFavorite } from "../../pages/favourite/hooks/useFavorite";
import { settingsItemOpen } from "../../pages/dashboard/store/modalSlice/modalSlice";
import { useDispatch } from "react-redux";
import { getFolderImage } from "../../store/slices/foldersSlice";
import { getRandomGif } from "../../helpers/getRandomGif";
import { getFilterStyle } from "../../helpers/filterStyles";

import addFav from "../../pages/dashboard/assets/icons/hurt_add.png";
import asFav from "../../assets/icons/hurt_fav.png";
import settings from "../../assets/icons/equalizer-line.png";
import remove from "../../assets/icons/delete-bin-line.png";

import "./style.css";

export const ImageCard = ({ item, onRemove, inCatalog, inMostFav, inFav }) => {
  const { url, id, name, isFavorite, rotation, mimeType, width, height, template, filter } = item;
  const isVideo = mimeType && mimeType.startsWith('video/');
  const dispatch = useDispatch();
  const { addToFavorite, removeFromFav } = useFavorite();

  const [isHovered, setIsHovered] = React.useState(false);
  const [addToFav, setAddToFav] = React.useState(isFavorite);
  const [mask, setMask] = React.useState(false);

  // Validate URL - use placeholder if invalid
  const validUrl = url && typeof url === 'string' && url.trim() !== '' ? url : getRandomGif();

  const currentTemplate = template || "1up";
  const currentFilter = filter || "";
  const filterStyle = getFilterStyle(currentFilter);

  const maskOpen = () => {
    setMask(true);
  };

  const maskClose = () => {
    setMask(false);
    setIsHovered(false);
  };

  const handleAddToFav = async (e) => {
    e.stopPropagation();
    await addToFavorite(id);
    setAddToFav(true);
  };

  const handleRemoveFromFav = async (e) => {
    e.stopPropagation();
    await removeFromFav(id);
    setAddToFav(false);
  };

  const handleImageSettingsOpen = (e) => {
    dispatch(getFolderImage(id));
    dispatch(settingsItemOpen());
  };

  const mediaStyle = {
    ...filterStyle,
    opacity: isHovered ? "0.2" : "1",
    transform: `rotate(${rotation}deg)`,
  };

  const renderMediaElement = (additionalClassName = "", additionalStyle = {}) => {
    const style = { ...mediaStyle, ...additionalStyle };
    const className = `image_card__template-item ${additionalClassName}`.trim();

    if (isVideo) {
      return (
        <video
          src={validUrl}
          className={className}
          style={style}
          muted
          playsInline
        />
      );
    }
    return (
      <img
        src={validUrl}
        alt={name}
        className={className}
        style={style}
        onError={(e) => {
          if (e.target.src !== getRandomGif()) {
            e.target.src = getRandomGif();
          }
        }}
      />
    );
  };

  const renderTemplatePreview = () => {
    const baseClassName = "image_card__template";
    const templateClassName = `${baseClassName} ${baseClassName}--${currentTemplate}`;

    if (currentTemplate === "1up") {
      return (
        <div className={templateClassName} onClick={maskOpen}>
          {renderMediaElement()}
        </div>
      );
    } else if (currentTemplate === "n2Up" || currentTemplate === "2next") {
      return (
        <div className={templateClassName} onClick={maskOpen}>
          <div className="image_card__template-item-wrapper">
            {renderMediaElement()}
          </div>
          <div className="image_card__template-item-wrapper">
            {renderMediaElement()}
          </div>
        </div>
      );
    } else if (currentTemplate === "n2Up-mirror-left") {
      return (
        <div className={templateClassName} onClick={maskOpen}>
          <div className="image_card__template-item-wrapper">
            {renderMediaElement("image_card__template-item--mirror")}
          </div>
          <div className="image_card__template-item-wrapper">
            {renderMediaElement()}
          </div>
        </div>
      );
    } else if (currentTemplate === "n2Up-mirror-right") {
      return (
        <div className={templateClassName} onClick={maskOpen}>
          <div className="image_card__template-item-wrapper">
            {renderMediaElement()}
          </div>
          <div className="image_card__template-item-wrapper">
            {renderMediaElement("image_card__template-item--mirror")}
          </div>
        </div>
      );
    } else if (currentTemplate === "4up" || currentTemplate === "4next") {
      return (
        <div className={templateClassName} onClick={maskOpen}>
          {[0, 1, 2, 3].map((idx) => (
            <div key={idx} className="image_card__template-item-wrapper">
              {renderMediaElement()}
            </div>
          ))}
        </div>
      );
    } else if (currentTemplate === "4up-warhol") {
      return (
        <div className={templateClassName} onClick={maskOpen}>
          {[0, 1, 2, 3].map((idx) => (
            <div key={idx} className="image_card__template-item-wrapper">
              {renderMediaElement(`image_card__template-item--warhol-${idx}`)}
            </div>
          ))}
        </div>
      );
    }

    // Fallback to 1up
    return (
      <div className={templateClassName} onClick={maskOpen}>
        {renderMediaElement()}
      </div>
    );
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={isHovered ? "hover" : "image_card"}
    >
      {renderTemplatePreview()}
      {isHovered && (
        <>
          {!inMostFav && (
            <img
              className={"preview__remove"}
              src={remove}
              alt={"remove"}
              onClick={onRemove}
            />
          )}
          <span className={"preview__gif"} onClick={maskOpen}>
            <EyeFilled className={"icon"} /> Preview
          </span>
          {inCatalog ? (
            addToFav ? (
              <img
                className={"preview__favorite"}
                src={asFav}
                alt="hart_add"
                onClick={(e) => handleRemoveFromFav(e)}
              />
            ) : (
              <img
                className={"preview__favorite"}
                src={addFav}
                alt="hurt_fav"
                onClick={(e) => handleAddToFav(e)}
              />
            )
          ) : (
            <img className={"fav_gif"} src={asFav} alt="hart_add" />
          )}

          {!inFav && (
            <img
              className={"preview__link"}
              src={settings}
              alt="link"
              onClick={handleImageSettingsOpen}
            />
          )}
        </>
      )}
      {mask && (
        <ImageMask
          close={maskClose}
          url={validUrl}
          name={name}
          id={id}
          deg={rotation}
          mimeType={mimeType}
          width={width}
          height={height}
          template={currentTemplate}
          filter={currentFilter}
        />
      )}
    </div>
  );
};
